import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { clientAPI } from "@/services/api";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

const ClientPaymentReturn = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reference = params.get("reference") || params.get("trxref");
  const [status, setStatus] = useState("pending");
  const [message, setMessage] = useState(
    "Waiting for Paystack's signed webhook confirmation…"
  );

  useEffect(() => {
    if (!reference) {
      setStatus("failed");
      setMessage("No payment reference was supplied.");
      return undefined;
    }

    let stopped = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const response = await clientAPI.getPaymentIntent(reference);
        const nextStatus = response.data?.status || "pending";
        if (stopped) return;
        setStatus(nextStatus);
        if (nextStatus === "paid") {
          setMessage("Escrow funding is confirmed and settled.");
          return;
        }
        if (["failed", "expired"].includes(nextStatus)) {
          setMessage(`Payment ${nextStatus}. Start a new funding attempt.`);
          return;
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setStatus("failed");
          setMessage("This payment reference does not belong to your account.");
          return;
        }
      }
      if (!stopped && attempts < 60) window.setTimeout(poll, 2000);
      if (!stopped && attempts >= 60) {
        setMessage("Confirmation is taking longer than expected. You can safely check again later.");
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [reference]);

  const Icon =
    status === "paid" ? CheckCircle2 : status === "pending" ? Clock : XCircle;

  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      <Icon
        className={`mx-auto h-12 w-12 ${
          status === "paid"
            ? "text-emerald-600"
            : status === "pending"
              ? "animate-pulse text-amber-600"
              : "text-rose-600"
        }`}
      />
      <h1 className="mt-4 text-xl font-semibold">Escrow payment</h1>
      <p className="mt-2 text-sm text-gray-600">{message}</p>
      <p className="mt-3 break-all font-mono text-xs text-gray-400">{reference}</p>
      <Button className="mt-6" onClick={() => navigate("/client/campaigns")}>
        View Prove-Requests
      </Button>
    </div>
  );
};

export default ClientPaymentReturn;
